/**
 * VoiceMode store — leaf module (Perf 3.1.b Step 5).
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { getStorage } from './storage';

const VOICE_MODE_STATES = {
  IDLE: 'idle',
  CONNECTING: 'connecting',
  RESUMING: 'resuming', // Attempting to resume a disconnected session
  LISTENING: 'listening',
  PROCESSING: 'processing',
  SPEAKING: 'speaking',
  ERROR: 'error',
};

// Default OpenAI Realtime voices (will be replaced by API response from /api/voice/voices)
const DEFAULT_VOICES = [
  { id: 'marin', name: 'Marin', gender: 'neutral', description: 'Warm, natural conversational voice' },
  { id: 'cedar', name: 'Cedar', gender: 'neutral', description: 'Smooth and friendly' },
  { id: 'ash', name: 'Ash', gender: 'neutral', description: 'Calm and measured' },
  { id: 'ballad', name: 'Ballad', gender: 'neutral', description: 'Expressive and emotive' },
  { id: 'coral', name: 'Coral', gender: 'neutral', description: 'Bright and upbeat' },
  { id: 'sage', name: 'Sage', gender: 'neutral', description: 'Soft and thoughtful' },
  { id: 'shimmer', name: 'Shimmer', gender: 'neutral', description: 'Light and energetic' },
  { id: 'verse', name: 'Verse', gender: 'neutral', description: 'Versatile and dynamic' },
];

// Fallback only — the authoritative rate comes from the backend session
// response (`config.pricing.creditsPerMinute`). Keep this in sync with
// `voiceMode.creditsPerMinute` in backend/config.js so the credit display
// looks correct during the brief window before the server config arrives.
const CREDITS_PER_MINUTE = 35;
const HEARTBEAT_INTERVAL_MS = 30000; // Send heartbeat every 30 seconds

const useVoiceModeStore = create(
  persist(
    (set, get) => ({
      // State
      isActive: false,
      state: VOICE_MODE_STATES.IDLE,
      selectedVoice: 'marin', // OpenAI Realtime default voice
      userTranscript: '',
      aiTranscript: '',
      partialTranscript: '',
      durationSeconds: 0,
      creditsUsed: 0,
      audioLevel: 0, // 0-1 for visualizations
      error: null,
      hasUsedVoiceMode: false,
      isMuted: false,
      conversationId: null,
      
      // Session state (from API)
      session: null, // { id, token, startedAt }
      config: null,  // { stt, tts, pricing } from API
      
      // Credits state (from API)
      creditsAvailable: 0,
      estimatedMinutesRemaining: 0,
      lowCreditsWarning: false,
      shouldEndSession: false,
      
      // Voice list state
      voices: DEFAULT_VOICES,
      voicesLoading: false,
      
      // Config
      creditsPerMinute: CREDITS_PER_MINUTE,
      
      // All media items (generating + complete) in this voice session
      // Each item: { id, type, status, progress, message, media, error, timestamp, prompt }
      mediaItems: [],
      
      // All output items (text, code) in this voice session
      // Each item: { id, type: 'text'|'code', content, language?, timestamp, status: 'streaming'|'complete' }
      outputItems: [],

      // Clickable web results for the current turn when Realtime calls web_search
      // { query, results: [{title,url,hostname,snippet,date}], citations, status: 'idle'|'searching'|'ready'|'empty'|'error' }
      searchResults: null,
      
      // ID of the focused/displayed item in the main view (can be media or output)
      focusedMediaId: null,
      
      // Vision state - allows AI to see through camera or screen share
      visionActive: false,
      visionSource: null, // 'camera' | 'screen' | null
      visionSessionId: null,
      visionFramesAnalyzed: 0,
      visionCreditsUsed: 0,
      visionError: null,
      visualContext: null, // Current visual context description from Gemini
      
      // Actions
      actions: {
        // Start voice mode - initiates the session
        startVoiceMode: (conversationId = null) => {
          set({ 
            isActive: true, 
            state: VOICE_MODE_STATES.CONNECTING,
            error: null,
            userTranscript: '',
            aiTranscript: '',
            partialTranscript: '',
            durationSeconds: 0,
            creditsUsed: 0,
            conversationId,
            session: null,
            config: null,
            lowCreditsWarning: false,
            shouldEndSession: false,
            // Reset media and output state
            mediaItems: [],
            outputItems: [],
            focusedMediaId: null,
            searchResults: null,
            // Reset vision state
            visionActive: false,
            visionSource: null,
            visionSessionId: null,
            visionFramesAnalyzed: 0,
            visionCreditsUsed: 0,
            visionError: null,
            visualContext: null,
          });
          return true;
        },
        
        // Stop voice mode - cleanup session
        stopVoiceMode: () => {
          const currentSession = get().session;
          const currentDuration = get().durationSeconds;
          const currentConversationId = get().conversationId;
          
          set({ 
            isActive: false, 
            state: VOICE_MODE_STATES.IDLE,
            audioLevel: 0,
            partialTranscript: '',
            isMuted: false,
            session: null,
            config: null,
            // IMPORTANT: Clear conversationId to prevent reusing old conversation on next voice chat
            conversationId: null,
            // Clear media and output state
            mediaItems: [],
            outputItems: [],
            focusedMediaId: null,
            searchResults: null,
            // Clear vision state
            visionActive: false,
            visionSource: null,
            visionSessionId: null,
            visionFramesAnalyzed: 0,
            visionCreditsUsed: 0,
            visionError: null,
          });
          
          // Return session info for cleanup in component
          return { sessionId: currentSession?.id, durationSeconds: currentDuration, conversationId: currentConversationId };
        },
        
        // Set session data from API
        setSession: (session, config, credits) => {
          set({ 
            session,
            config,
            creditsAvailable: credits?.available || 0,
            estimatedMinutesRemaining: credits?.estimatedMinutes || credits?.estimatedMinutesRemaining || 0,
            creditsPerMinute: config?.pricing?.creditsPerMinute || CREDITS_PER_MINUTE,
          });
        },
        
        // Update credits from heartbeat response
        updateCredits: (credits, warnings) => {
          set({
            creditsAvailable: credits?.available || 0,
            creditsUsed: credits?.used || 0,
            estimatedMinutesRemaining: credits?.estimatedMinutesRemaining || 0,
            lowCreditsWarning: warnings?.lowCredits || false,
            shouldEndSession: warnings?.shouldEnd || false,
          });
        },
        
        // Set voices from API
        setVoices: (voices, defaultVoiceId) => {
          const currentVoice = get().selectedVoice;
          const voiceExists = voices.some(v => v.id === currentVoice);
          
          set({ 
            voices,
            // Only update selected voice if current doesn't exist in new list
            selectedVoice: voiceExists ? currentVoice : (defaultVoiceId || voices[0]?.id),
            voicesLoading: false,
          });
        },
        
        setVoicesLoading: (loading) => {
          set({ voicesLoading: loading });
        },
        
        setState: (newState) => {
          set({ state: newState });
        },
        
        setUserTranscript: (transcript) => {
          set({ userTranscript: transcript });
        },
        
        setAiTranscript: (transcript) => {
          set({ aiTranscript: transcript });
        },
        
        setPartialTranscript: (transcript) => {
          set({ partialTranscript: transcript });
        },
        
        appendUserTranscript: (text) => {
          set(state => ({ 
            userTranscript: state.userTranscript + (state.userTranscript ? ' ' : '') + text 
          }));
        },
        
        appendAiTranscript: (text) => {
          set(state => ({ 
            aiTranscript: state.aiTranscript + text 
          }));
        },
        
        // Clear AI transcript (for barge-in)
        clearAiTranscript: () => {
          set({ aiTranscript: '' });
        },
        
        setAudioLevel: (level) => {
          set({ audioLevel: Math.max(0, Math.min(1, level)) });
        },
        
        setSelectedVoice: (voiceId) => {
          set({ selectedVoice: voiceId });
        },
        
        toggleMute: () => {
          set(state => ({ isMuted: !state.isMuted }));
        },
        
        setMuted: (isMuted) => {
          set({ isMuted });
        },
        
        incrementDuration: () => {
          const creditsPerMinute = get().creditsPerMinute || CREDITS_PER_MINUTE;
          set(state => {
            const newDuration = state.durationSeconds + 1;
            const newCreditsUsed = Math.ceil((newDuration / 60) * creditsPerMinute);
            return { 
              durationSeconds: newDuration,
              creditsUsed: newCreditsUsed,
            };
          });
        },
        
        setError: (error) => {
          set({ 
            state: VOICE_MODE_STATES.ERROR, 
            error: typeof error === 'string' ? error : error?.message || 'An error occurred',
          });
        },
        
        clearError: () => {
          set({ error: null, state: VOICE_MODE_STATES.IDLE });
        },
        
        markAsUsed: () => {
          set({ hasUsedVoiceMode: true });
        },
        
        clearTranscripts: () => {
          set({ 
            userTranscript: '', 
            aiTranscript: '', 
            partialTranscript: '' 
          });
        },
        
        setConversationId: (conversationId) => {
          set({ conversationId });
        },
        
        // Reset to start new conversation turn
        startNewTurn: () => {
          set({
            userTranscript: '',
            aiTranscript: '',
            partialTranscript: '',
            state: VOICE_MODE_STATES.LISTENING,
          });
        },
        
        // Media generation actions - supports multiple simultaneous generations
        
        // Start a new media generation - adds item to mediaItems, sets as focused
        setMediaStarted: (type, prompt) => {
          const newId = `media_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
          const newItem = {
            id: newId,
            type,
            status: 'generating',
            progress: 0,
            message: type === 'create-music' ? 'Composing...' : `Creating ${type.replace('create-', '')}...`,
            media: null,
            error: null,
            timestamp: Date.now(),
            prompt,
          };
          
          console.log('[Store] setMediaStarted - creating new item:', newId, type);
          
          set(state => {
            const currentItems = state.mediaItems || [];
            console.log('[Store] setMediaStarted - current items count:', currentItems.length);
            const newItems = [newItem, ...currentItems];
            console.log('[Store] setMediaStarted - new items count:', newItems.length);
            return {
              mediaItems: newItems,
              focusedMediaId: newId,
            };
          });
        },
        
        // Update progress for a specific item (by ID) or the most recent generating item
        setMediaProgress: (progress, message, itemId = null) => {
          set(state => {
            // Find the item to update - either by ID or the most recent generating item
            const targetId = itemId || state.mediaItems.find(item => item.status === 'generating')?.id;
            if (!targetId) return state;
            
            return {
              mediaItems: state.mediaItems.map(item => 
                item.id === targetId
                  ? {
                      ...item,
                      progress: Math.min(100, Math.max(0, progress)),
                      message: message || item.message,
                    }
                  : item
              ),
            };
          });
        },
        
        // Mark an item as complete (by ID) or the most recent generating item
        setMediaComplete: (type, media, itemId = null) => {
          console.log('[Store] setMediaComplete called - type:', type, 'itemId:', itemId);
          
          set(state => {
            const currentItems = state.mediaItems || [];
            console.log('[Store] setMediaComplete - current items:', currentItems.length, currentItems.map(i => ({ id: i.id, type: i.type, status: i.status })));
            
            // Find the item to complete - either by ID or the most recent generating item of this type
            let targetId = itemId;
            if (!targetId) {
              // Find the most recent generating item (optionally matching type)
              const generatingItem = currentItems.find(item => 
                item.status === 'generating' && (!type || item.type === type)
              );
              targetId = generatingItem?.id;
              console.log('[Store] setMediaComplete - found generating item:', targetId);
            }
            if (!targetId) {
              console.log('[Store] setMediaComplete - no target found, returning unchanged state');
              return state;
            }
            
            const updatedItems = currentItems.map(item =>
              item.id === targetId
                ? {
                    ...item,
                    status: 'complete',
                    progress: 100,
                    message: '',
                    media,
                  }
                : item
            );
            
            console.log('[Store] setMediaComplete - updated items:', updatedItems.length, updatedItems.map(i => ({ id: i.id, type: i.type, status: i.status })));
            
            return {
              mediaItems: updatedItems,
              // Keep or set focus to the completed item
              focusedMediaId: state.focusedMediaId || targetId,
            };
          });
        },
        
        // Mark an item as error (by ID) or the most recent generating item
        setMediaError: (error, itemId = null) => {
          set(state => {
            const targetId = itemId || state.mediaItems.find(item => item.status === 'generating')?.id;
            if (!targetId) return state;
            
            return {
              mediaItems: state.mediaItems.map(item =>
                item.id === targetId
                  ? {
                      ...item,
                      status: 'error',
                      error: typeof error === 'string' ? error : error?.message || 'Generation failed',
                    }
                  : item
              ),
            };
          });
        },
        
        // Set accent color for an item
        setMediaAccentColor: (accentColor, itemId = null) => {
          set(state => {
            const targetId = itemId || state.focusedMediaId;
            if (!targetId) return state;
            
            return {
              mediaItems: state.mediaItems.map(item =>
                item.id === targetId
                  ? { ...item, accentColor }
                  : item
              ),
            };
          });
        },
        
        // Set focused media (when user taps a thumbnail)
        setFocusedMedia: (item) => {
          if (!item?.id) return;
          set({ focusedMediaId: item.id });
        },
        
        // Clear only the focused display (doesn't remove items)
        clearFocusedMedia: () => {
          set({ focusedMediaId: null });
        },
        
        // Clear all media items
        clearMediaItems: () => {
          set({
            mediaItems: [],
            focusedMediaId: null,
          });
        },
        
        // Output item actions (text, code)
        
        // Add a new output item (text or code block)
        addOutputItem: (type, content, language = null) => {
          const newId = `output_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
          const newItem = {
            id: newId,
            type, // 'text' or 'code'
            content,
            language, // For code blocks: 'javascript', 'python', etc.
            timestamp: Date.now(),
            status: 'complete',
          };
          
          set(state => ({
            outputItems: [newItem, ...state.outputItems],
            focusedMediaId: newId,
          }));
          
          return newId;
        },
        
        // Update an existing output item (for streaming)
        updateOutputItem: (itemId, content) => {
          set(state => ({
            outputItems: state.outputItems.map(item =>
              item.id === itemId
                ? { ...item, content, status: 'streaming' }
                : item
            ),
          }));
        },
        
        // Mark an output item as complete (for streaming)
        completeOutputItem: (itemId) => {
          set(state => ({
            outputItems: state.outputItems.map(item =>
              item.id === itemId
                ? { ...item, status: 'complete' }
                : item
            ),
          }));
        },
        
        // Clear all output items
        clearOutputItems: () => {
          set({ outputItems: [] });
        },
        
        // Clear all items (media + output)
        clearAllItems: () => {
          set({
            mediaItems: [],
            outputItems: [],
            focusedMediaId: null,
          });
        },

        // ==================== Voice Web Search (SERP) ====================

        setSearchResultsSearching: (query = '') => {
          set({
            searchResults: {
              query: typeof query === 'string' ? query : '',
              results: [],
              citations: [],
              status: 'searching',
              updatedAt: Date.now(),
            },
          });
        },

        setSearchResults: (payload) => {
          const results = Array.isArray(payload?.results) ? payload.results : [];
          const citations = Array.isArray(payload?.citations) ? payload.citations : [];
          set({
            searchResults: {
              query: typeof payload?.query === 'string' ? payload.query : '',
              results,
              citations,
              status: results.length > 0 ? 'ready' : 'empty',
              updatedAt: Date.now(),
            },
          });
        },

        setSearchResultsError: (message) => {
          set({
            searchResults: {
              query: '',
              results: [],
              citations: [],
              status: 'error',
              error: typeof message === 'string' ? message : 'Search failed',
              updatedAt: Date.now(),
            },
          });
        },

        clearSearchResults: () => {
          set({ searchResults: null });
        },
        
        // Get all items (media + output) sorted by timestamp for timeline
        getAllItems: () => {
          const state = get();
          const allItems = [
            ...(state.mediaItems || []).map(item => ({ ...item, itemType: 'media' })),
            ...(state.outputItems || []).map(item => ({ ...item, itemType: 'output' })),
          ];
          // Sort by timestamp, newest first
          return allItems.sort((a, b) => b.timestamp - a.timestamp);
        },
        
        // Legacy aliases for compatibility
        clearMedia: () => {
          set({ focusedMediaId: null });
        },
        
        clearMediaHistory: () => {
          set({
            mediaItems: [],
            focusedMediaId: null,
          });
        },
        
        // ==================== Vision Actions ====================
        
        // Start vision session (camera or screen share)
        startVision: (source, visionSessionId) => {
          set({
            visionActive: true,
            visionSource: source, // 'camera' or 'screen'
            visionSessionId,
            visionFramesAnalyzed: 0,
            visionCreditsUsed: 0,
            visionError: null,
          });
        },
        
        // Stop vision session
        stopVision: () => {
          set({
            visionActive: false,
            visionSource: null,
            visionSessionId: null,
            visionError: null,
            visualContext: null,
          });
        },
        
        // Update vision frame count and credits
        updateVisionStats: (framesAnalyzed, creditsUsed) => {
          set(state => ({
            visionFramesAnalyzed: framesAnalyzed ?? state.visionFramesAnalyzed,
            visionCreditsUsed: creditsUsed ?? state.visionCreditsUsed,
          }));
        },
        
        // Increment frame count (called after each successful frame analysis)
        incrementVisionFrame: (creditsCost = 0.5) => {
          set(state => ({
            visionFramesAnalyzed: state.visionFramesAnalyzed + 1,
            visionCreditsUsed: state.visionCreditsUsed + creditsCost,
          }));
        },
        
        // Set vision error
        setVisionError: (error) => {
          set({
            visionError: typeof error === 'string' ? error : error?.message || 'Vision error',
          });
        },
        
        // Clear vision error
        clearVisionError: () => {
          set({ visionError: null });
        },
        
        // Set visual context from Gemini analysis
        setVisualContext: (context) => {
          set({ visualContext: context });
        },
        
        // Clear visual context
        clearVisualContext: () => {
          set({ visualContext: null });
        },
      },
    }),
    {
      name: 'voice-mode-storage',
      storage: createJSONStorage(() => getStorage()),
      partialize: (state) => ({
        selectedVoice: state.selectedVoice,
        hasUsedVoiceMode: state.hasUsedVoiceMode,
      }),
    }
  )
);

export { useVoiceModeStore };
export { VOICE_MODE_STATES, DEFAULT_VOICES, CREDITS_PER_MINUTE, HEARTBEAT_INTERVAL_MS };
export default useVoiceModeStore;
